import { Children, forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import cx from 'classnames';
import useForceUpdate from '../../hooks/useForceUpdate';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { useRefArray } from '../../hooks/useRefArray';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { convertCSSSizeToNumber } from '../../util/dom';
import { registerDependency } from '../../util/ServiceDependencies';
import { SplitterSection } from './__internal__/SplitterSection';
import { usePersistedSplitter } from './persist/PersistedSplitterProvider';
import bundle from './NovaComponents-Splitter-gui-icu.properties';
import cn from './Splitter.css.json';
import './Splitter.css';
import { flushSync } from 'react-dom';

// Register Splitter component so that it will fetch required preference data
// eslint-disable-next-line @sas/i18n/jsx-to-localize
import { jsx as _jsx } from "react/jsx-runtime";
registerDependency('Splitter');
const useTextLocalizer = createUseTextLocalizer(bundle);
const logger = getLogger('Splitter');
export const Splitter = /*#__PURE__*/forwardRef(({
  persistPaneSizes = true,
  ...props
}, ref) => {
  const isRTL = useIsRTL();
  const persistService = props.dataProvider ?? usePersistedSplitter();
  const orientation = props.orientation || 'horizontal';
  const sectionToggled = useRef(false);
  const sectionResized = useRef(false);
  let barCount = 0;
  const localize = useTextLocalizer();
  const [setSplitterContainer, getSplitterContainer] = useResizeObserverRef(() => {
    const container = getSplitterContainer();
    if (container) setContainerSize(container.getBoundingClientRect()[orientation === 'horizontal' ? 'width' : 'height']);
  });
  const sectionBarPositions = useMemo(() => {
    if (!props.sections) return [];
    // Collapsible property decides the arrow position on splitter bar
    // First section which has collapsible=true get first preference as collapsible

    let barCount = 0;
    return props.sections.map((section, index) => {
      const nextSection = props.sections ? props.sections[index + 1] : undefined;
      if (props.sections && props.sections.length >= 2 && (section.collapsible || section.fullyCollapsible) && index === 0) {
        barCount += 1;
        return 'right';
      } else if (props.sections?.length === 2 && (section.collapsible || section.fullyCollapsible) && index === 1 && barCount === 0) {
        return 'left';
      } else if (props.sections?.length === 2 && !(section.collapsible || section.fullyCollapsible) && index === 0 && nextSection && !(nextSection.collapsible || nextSection.fullyCollapsible)) {
        return 'right';
      } else if (props.sections?.length === 3 && index === 2) {
        return 'left';
      } else if (props.sections?.length === 3 && index === 0 && !!!section.collapsible && !!!section.fullyCollapsible) {
        if (!!!nextSection?.collapsible && !!!nextSection?.fullyCollapsible) {
          return 'right';
        } else {
          return undefined;
        }
      } else if (props.sections && props.sections.length > 3) {
        if (index === 0) return 'right';
        if (props.sections.length - 1 === index) {
          return 'left';
        } else if (props.sections.length - 2 === index) {
          return undefined;
        } else {
          return 'right';
        }
      } else {
        return undefined;
      }
    });
  }, [props.sections]);
  const [isDragging, setIsDragging] = useState(false);
  const [containerSize, setContainerSize] = useState(undefined);
  const {
    refs,
    createSetRef
  } = useRefArray();
  const getSectionSizes = useCallback(() => {
    return refs.map(element => {
      if (!element) return NaN;
      const bounds = element.getBoundingClientRect();
      return bounds[orientation === 'horizontal' ? 'width' : 'height'];
    });
  }, [refs, orientation]);

  // represents the persisted dimension of the Splitter panes.
  const persistedDimensions = useRef({
    paneSizes: [],
    previousBarPositions: []
  });
  const rerenderOnFetch = useForceUpdate();
  const [{
    sizes,
    previousSizes
  }, setSizes] = useState(() => {
    if (!props.sections) {
      return {
        sizes: [],
        previousSizes: []
      };
    } else {
      return {
        sizes: props.sections.map(section => {
          if (section.defaultSize) {
            const minSize = convertCSSSizeToNumber(section.minSize);
            if (section.defaultSize.indexOf('%') > -1) {
              return minSize;
            } else {
              return Math.max(convertCSSSizeToNumber(section.defaultSize), minSize);
            }
          }
          return -1;
        }),
        previousSizes: []
      };
    }
  });
  useEffect(() => {
    async function fetchPersistedSize(id) {
      try {
        const persistedSize = await persistService?.getSize(id);
        if (persistedSize) {
          // incase the GET Size is delayed and user has changed the size,
          //  we do not want to store and apply older values
          if (persistedDimensions.current.paneSizes.length === 0) {
            persistedDimensions.current = persistedSize;
            rerenderOnFetch();
          }
        }
      } catch (e) {
        logger.error(`fetch persisted pane sizes for splitter with id ${id} failed`);
      }
    }
    if (persistPaneSizes && persistService && props.id) {
      fetchPersistedSize(props.id);
    }
  }, [persistPaneSizes, persistService, props.id, props.sections, rerenderOnFetch]);
  const updateSplitterPreferenceData = (id, oSplitterSizes) => {
    // the persistedDimensions ref is reflected with the changed Size regardless of persistence
    persistedDimensions.current = oSplitterSizes;
    persistService?.setSize(id, oSplitterSizes).catch(() => logger.error(`Save pane sizes for splitter with id ${id} failed`));
  };
  const convertPersistedSizesToPixels = (paneSizesInPercentage, containerSize, sections) => {
    // If a section has to be expanded to the min size, others have to be shrunk to avoid overlap
    const paneSizesInPixels = allocateWithProportions(paneSizesInPercentage.map(paneSize => paneSize && paneSize.indexOf('%') > -1 && paneSize.lastIndexOf('%') === paneSize.length - 1 ? parseFloat(paneSize) / 100 : 0), containerSize ? containerSize : 1, sections);
    return paneSizesInPixels;
  };
  const convertPropsSizesToPixels = (sections, containerSize) => {
    const paneSizesInPixels = sections.map(section => {
      const minSize = convertCSSSizeToNumber(section.minSize);
      if (!!section.initialSplitterBarCollapsed && !isRenderedByProps.current) {
        if (section.fullyCollapsible) return 0;
        return minSize;
      }
      let size = -1;
      if (section.defaultSize && section.defaultSize.indexOf('%') > -1 && section.defaultSize.lastIndexOf('%') === section.defaultSize.length - 1) {
        size = parseInt(section.defaultSize) / 100 * (containerSize ? containerSize : 1);
      } else if (section.defaultSize && section.defaultSize.indexOf('px') > -1 && section.defaultSize.lastIndexOf('px') === section.defaultSize.length - 2) {
        size = parseInt(section.defaultSize);
      } else if (section.defaultSize && section.defaultSize.indexOf('rem') > -1 && section.defaultSize.lastIndexOf('rem') === section.defaultSize.length - 3) {
        size = convertCSSSizeToNumber(section.defaultSize);
      }
      return size === -1 ? -1 : Math.max(size, minSize);
    });
    return paneSizesInPixels;
  };

  // When reallocate the size with the exact size of each section, the sum of the calculated sizes are supposed to be 100% of the new container size.
  // If a section has to be expanded to the min size, others have to be shrunk to avoid overlap.
  const allocateWithProportions = (proportions, containerSize, sections) => {
    const deltas = proportions.map((proportion, index) => {
      const minSize = convertCSSSizeToNumber(sections?.[index]?.minSize);
      if (proportion === undefined) {
        return 0;
      } else if (proportion === 0) {
        // section is collapsed
        return null;
      } else if (proportion < 0) {
        // section has not default size, implies it is 'auto'
        return undefined;
      } else {
        return proportion * containerSize - minSize;
      }
    });

    // totalNegativeDelta is always <= 0
    let totalNegativeDelta = deltas.reduce((a, b) => {
      if (b === undefined || b === null) {
        return a;
      } else if (b < 0) {
        return a + b;
      } else {
        return a;
      }
    }, 0);
    const newSizes = deltas.map((delta, index) => {
      const minSize = convertCSSSizeToNumber(sections?.[index]?.minSize);
      if (delta === undefined) {
        return -1;
      } else if (delta === null) {
        return 0;
      } else if (delta <= 0) {
        return minSize;
      } else {
        if (delta >= -totalNegativeDelta) {
          const newSize = minSize + delta + totalNegativeDelta;
          totalNegativeDelta = 0;
          return newSize;
        } else {
          totalNegativeDelta += delta;
          return minSize;
        }
      }
    });
    return newSizes;
  };
  useLayoutEffect(() => {
    if (containerSize && props.id && props.sections && persistedDimensions.current.paneSizes.length > 0) {
      // If spliiter's pane sizes are persisted against its id
      const paneSizes = persistedDimensions.current.paneSizes;
      const previousPaneSizes = persistedDimensions.current.previousBarPositions;

      // Convert persisted pane size proportions to pixels.
      const persistedSizes = convertPersistedSizesToPixels(paneSizes, containerSize, props.sections);
      const persistedPrevSizes = convertPersistedSizesToPixels(previousPaneSizes, containerSize, props.sections);
      setSizes(() => ({
        sizes: persistedSizes,
        previousSizes: persistedPrevSizes
      }));
    } else if (containerSize && props.sections) {
      if (oldContainerSize) {
        /**
         * If a section has to be expanded to the min size, others have to be shrunk to avoid overlap
         *
         * If the current size of a section is the same as the oldContainerSize, then we need to subtract the splitterBarSize and then divide by the oldContainerSize
         * so that the proportion of that section won't reach '1'. If the proportion is made '1' then the section will take up the entire space of the viewport
         * and the splitter bar will not be visible in the viewport making it impossible to resize the sections via the mouse.
         */
        let calculatedSizes = allocateWithProportions(sizes.map(size => size < 0 ? size : (size === oldContainerSize ? size - splitterBarSize : size) / oldContainerSize * 100 / 100), containerSize, props.sections);
        const totalCalculatedSizes = calculatedSizes.reduce((prev, curr) => prev + curr, 0); // Get the total of the newly calculated sizes
        const totalPlusBars = totalCalculatedSizes + splitterBarSize * (props.sections.length - 1); // Add the splitterBarSize of all the splitter bars rendered in the component. No. of splitter bars = (No. of sections - 1)
        const diff = totalPlusBars - containerSize; // Get the difference between the total of the newly calculated sizes and the containerSize
        // Check if all sections have reacehed their respective minSize
        const allSectionsReachedMinSize = calculatedSizes.every((size, index) => props.sections && size === props.sections[index].minSize);

        /*
          We need to adjust the calculatedSizes if the below 3 conditions are met:
          1. The total size of all the sections and the splitter bars combined is greater than the containerSize.
          2. The difference between the totalSizes and the containerSize is greater than 0.5px.
          3. Atlease 1 of the newly calculatedSizes of the sections is not equal to the minSize of that section. If all the sections have reached their minSize, we don't need to adjust the containerSize.
           If all the above conditions are met, we need to adjust the calculatedSizes by removing (diff / numberOfSections) from each section so the total section sizes and splitter bar sizes combined are less than or equal to the containerSize.
        */
        if (totalPlusBars > containerSize && diff >= 0.5 && !allSectionsReachedMinSize) {
          const diffToSubtract = diff / props.sections.length; // Calculate the size to be subtracted from each section.
          calculatedSizes = calculatedSizes.map(size => size - diffToSubtract); // Adjust the calculatedSizes
        }

        setSizes(({
          previousSizes
        }) => ({
          sizes: calculatedSizes,
          previousSizes
        }));
      } else {
        const cssSizes = convertPropsSizesToPixels(props.sections, containerSize);
        isRenderedByProps.current = true;
        setSizes(({
          previousSizes
        }) => ({
          sizes: cssSizes,
          previousSizes
        }));
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerSize, props.id]);
  const canPaneSizesBePersisted = persistPaneSizes && persistService && props.id;

  // sectionToggled and sectionResized are set to true if canPaneSizesBePersisted is positive

  /**
   * This useEffect hook will fire PUT call to update pane sizes in preference service
   */
  useEffect(() => {
    if (!isDragging && props.id) {
      if (sectionToggled.current && JSON.stringify(oldSizes) !== JSON.stringify(sizes)) {
        sectionToggled.current = false;
        // Save preferences in % dimension if containerSize is undefined then store it in px dimension
        const oSplitterSizes = {
          paneSizes: sizes.map(sizeInPX => sizeInPX && containerSize ? `${sizeInPX / containerSize * 100}%` : `${sizeInPX}px`),
          previousBarPositions: previousSizes.map(sizeInPX => sizeInPX && containerSize ? `${sizeInPX / containerSize * 100}%` : `${sizeInPX}px`)
        };
        updateSplitterPreferenceData(props.id, oSplitterSizes);
      } else if (sectionResized.current) {
        sectionResized.current = false;
        // Save preferences in % dimension if containerSize is undefined then store it in px dimension
        const oSplitterSizes = {
          paneSizes: sizes.map(sizeInPX => sizeInPX && containerSize ? `${sizeInPX / containerSize * 100}%` : `${sizeInPX}px`),
          previousBarPositions: previousSizes.map(sizeInPX => sizeInPX && containerSize ? `${sizeInPX / containerSize * 100}%` : `${sizeInPX}px`)
        };
        updateSplitterPreferenceData(props.id, oSplitterSizes);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, sizes]);
  const isPaneCollapsed = index => {
    const section = props.sections?.[index];
    const minSize = convertCSSSizeToNumber(section?.minSize);
    const currentSize = sizes[index];
    return currentSize - minSize < 0.0001;
  };
  function resizeSection(action) {
    let deltaPX = action.deltaPX;
    // Do the resizing calculations only if deltaPX has some value
    if (deltaPX) {
      const next = getSectionSizes();
      const secondarySectionIndex = action.index + (action.direction === 'left' ? -1 : 1);
      if (props.sections) {
        // check minSize of the secondary section being resized
        {
          const section = props.sections[secondarySectionIndex];
          const nextSize = next[secondarySectionIndex] - deltaPX;
          const minSize = section.fullyCollapsible && !section.disableAutoClose ? 0 : secondarySectionIndex === 0 || secondarySectionIndex === Children.count(props.children) - 1 ? convertCSSSizeToNumber(section.minSize) : Math.max(0, paddingSize, convertCSSSizeToNumber(section.minSize));
          if (minSize > nextSize) {
            deltaPX -= minSize - nextSize;
          }
        }
        // check minSize of the primary section being resized
        {
          const section = props.sections[action.index];
          const nextSize = next[action.index] + deltaPX;
          const minSize = section.fullyCollapsible && !section.disableAutoClose ? 0 : action.index === 0 || action.index === Children.count(props.children) - 1 ? convertCSSSizeToNumber(section.minSize) : Math.max(0, paddingSize, convertCSSSizeToNumber(section.minSize));
          if (minSize > nextSize) {
            deltaPX += minSize - nextSize;
          }
        }
      }

      // If the section is fully collapsed and we try to drag it below 0, then don't move the splitter at all
      if (next[action.index] === 0 && action.deltaPX < 0) deltaPX = 0;
      next[action.index] += deltaPX;
      next[secondarySectionIndex] -= deltaPX;
      if (props.sections) {
        const section = props.sections[action.index];
        const minSize = convertCSSSizeToNumber(section.minSize);
        const currentSize = Math.round(sizes[action.index]);
        const currentSizeWithDeltaPX = currentSize + deltaPX;
        if (next[action.index] - minSize < 0.0001) {
          if (section.fullyCollapsible) {
            if (!section.disableAutoClose && !isPaneCollapsed(action.index) && deltaPX < 0) {
              next[action.index] = 0;
              if (currentSizeWithDeltaPX === minSize) {
                next[secondarySectionIndex] += minSize;
              } else {
                next[secondarySectionIndex] += currentSizeWithDeltaPX;
              }
            } else if (isPaneCollapsed(action.index) && deltaPX > 0) {
              next[action.index] = minSize;
              next[secondarySectionIndex] -= containerSize ?? 0;
            } else if (!section.disableAutoClose && currentSize === minSize && deltaPX < 0) {
              next[action.index] = 0;
              next[secondarySectionIndex] += currentSizeWithDeltaPX;
            }
          }
          props.onExpandCollapse?.(true, action.index);
        } else if (section.collapsible && next[action.index] < 10) {
          /*
            This part of code will only be triggered when the splitter is collapsible and is being dragged at the very start/end of the container.
            This normally happens when the minSize is 0 or undefined.
          */
          if (deltaPX < 0) {
            // As soon as the section size goes below 10px, we will close the section completely (size = 0). This prevents the collapse icon from truncating while collapsing the splitter bar.
            next[secondarySectionIndex] += next[action.index];
            next[action.index] = 0;
          } else if (deltaPX > 0) {
            // When we drag the splitter back outside, we set the size of the section to 10 px, so the collapse icon won't be truncated in the process of expanding the splitter bar.
            next[action.index] = 10;
            next[secondarySectionIndex] -= next[action.index];
          }
        } else {
          props.onExpandCollapse?.(false, action.index);
        }
      }
      props.onBarMove?.(next);
      flushSync(() => {
        setSizes(({
          previousSizes
        }) => ({
          sizes: next,
          previousSizes
        }));
      });
    }
    return deltaPX !== 0;
  }
  const splitterBarSize = 2;
  const containerMinSize = () => {
    let size = 0;
    props.sections && props.sections.forEach(section => {
      if (section.minSize) {
        size += convertCSSSizeToNumber(section.minSize);
      }
    });
    return size + barCount * splitterBarSize;
  };
  const dt = useDesignTokens();
  // Feature flag for 'New Token Values'

  const splitterSectionPadding = dt.tokens['space-base-s'];
  const splitterTokens = renderInlineCssVars({
    bg: dt.tokens['color-container-border-4'],
    'bar-size': `${splitterBarSize}px`,
    'min-size': `${containerMinSize()}px`,
    'icon-size': dt.tokens['size-icon-xxxl'],
    'icon-target-area-size': dt.tokens['space-base-l'],
    'section-padding': splitterSectionPadding,
    'outline-offset': '2px'
  }, 'splitter');
  const paddingSize = 2 * convertCSSSizeToNumber(splitterSectionPadding);
  const oldSizes = usePreviousValue(sizes);
  const oldContainerSize = usePreviousValue(containerSize);
  const oldDefaultValues = usePreviousValue(props.sections);
  const isRenderedByProps = useRef(false);
  const isRenderedByService = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (containerSize && persistPaneSizes && props.id && props.sections && !isRenderedByService.current && persistedDimensions.current.paneSizes.length > 0) {
      // If spliiter's pane sizes are persisted against its id
      const paneSizes = persistedDimensions.current.paneSizes;
      const previousPaneSizes = persistedDimensions.current.previousBarPositions;

      // Convert persisted pane size proportions to pixels.
      const persistedSizes = convertPersistedSizesToPixels(paneSizes, containerSize, props.sections);
      // Convert persisted pane prev size proportions to pixels.
      const persistedPrevSizes = convertPersistedSizesToPixels(previousPaneSizes, containerSize, props.sections);
      if (JSON.stringify(oldSizes) !== JSON.stringify(persistedSizes)) {
        // Apply persisted Splitter Pane sizes
        isRenderedByService.current = true;
        setSizes(() => ({
          sizes: persistedSizes,
          previousSizes: persistedPrevSizes
        }));
      }
    } else if (containerSize && props.sections && !isRenderedByProps.current && !isRenderedByService.current) {
      const cssSizes = convertPropsSizesToPixels(props.sections, containerSize);
      if (JSON.stringify(oldSizes) !== JSON.stringify(cssSizes)) {
        isRenderedByProps.current = true;
        setSizes(({
          previousSizes
        }) => ({
          sizes: cssSizes,
          previousSizes
        }));
      }
    } else if (JSON.stringify(oldDefaultValues) !== JSON.stringify(props.sections) && props.sections && containerSize) {
      const cssSizes = convertPropsSizesToPixels(props.sections, containerSize);
      if (JSON.stringify(oldSizes) !== JSON.stringify(cssSizes)) {
        setSizes(({
          previousSizes
        }) => ({
          sizes: cssSizes,
          previousSizes
        }));
      }
    }
  });
  useImperativeHandle(ref, () => ({
    togglePane,
    isPaneCollapsed
  }));
  const getSectionDefaultSizeInPixel = ({
    defaultSize
  }) => {
    if (!defaultSize) return -1;
    const isPercent = defaultSize.indexOf('%') > -1 && defaultSize.lastIndexOf('%') === defaultSize.length - 1;
    const isPixel = defaultSize.indexOf('px') > -1 && defaultSize.lastIndexOf('px') === defaultSize.length - 2;
    const isRem = defaultSize.indexOf('rem') > -1 && defaultSize.lastIndexOf('rem') === defaultSize.length - 3;
    if (isPercent) {
      return parseInt(defaultSize) / 100 * (containerSize ? containerSize : 1);
    } else if (isPixel) {
      return parseInt(defaultSize);
    } else if (isRem) {
      return convertCSSSizeToNumber(defaultSize);
    }
    return -1;
  };
  const togglePane = index => {
    const actSizes = getSectionSizes();
    setSizes(({
      sizes,
      previousSizes
    }) => {
      const section = props.sections?.[index];
      const nextSizes = [...sizes],
        nextPrevSizes = [...previousSizes];
      if (!previousSizes[index] && !!section?.initialSplitterBarCollapsed) {
        previousSizes[index] = getSectionDefaultSizeInPixel(section);
      }
      const minSizeToPX = convertCSSSizeToNumber(section?.minSize);
      const minSize = section?.fullyCollapsible ? 0 : minSizeToPX,
        // section?.minSize ?? 0,
        currentSize = sizes[index] === -1 ? actSizes[index] : sizes[index];

      // According to current index take according pane size (next or previous)
      const paneIndex = index === 0 ? 1 : index - 1;
      const paneSize = sizes[paneIndex] === -1 ? actSizes[paneIndex] : sizes[paneIndex];
      const paneMinSize = convertCSSSizeToNumber(props.sections?.[paneIndex]?.minSize);
      let deltaSize,
        toggleSize = minSize,
        prevSize = currentSize;
      if (section?.fullyCollapsible) {
        if (currentSize - minSize < 0.0001) {
          // section is already collapsed
          deltaSize = Math.min(previousSizes[index] ?? 0 - currentSize, paneSize - paneMinSize); // delta size cannot be greater than available
          toggleSize = deltaSize + currentSize;
        } else {
          deltaSize = currentSize;
          toggleSize = 0;
        }
      } else {
        deltaSize = currentSize > minSize ? currentSize - minSize : !isNaN(previousSizes[index]) ? Math.min(previousSizes[index] - minSize, paneSize - paneMinSize) // delta size cannot be greater than available
        : 0; // delta should be 0 instead of NaN when no previous size
      }

      if (deltaSize === 0) {
        deltaSize = section?.fullyCollapsible && minSizeToPX > 0 ? minSizeToPX : 20;
      }
      let isCollapsed = false;
      if (currentSize - minSize < 0.0001) {
        // section is already collapsed
        prevSize = minSize;
        toggleSize = deltaSize + currentSize;
        nextSizes[paneIndex] = paneSize - deltaSize;
      } else {
        nextSizes[paneIndex] = paneSize + deltaSize;
        isCollapsed = true;
      }
      nextPrevSizes[index] = prevSize;
      nextSizes[index] = toggleSize;
      sectionToggled.current = true;
      props.onExpandCollapse?.(isCollapsed, index);
      props.onBarMove?.(nextSizes);
      return Object.create({
        sizes: [...nextSizes],
        previousSizes: [...nextPrevSizes]
      });
    });
  };
  const calculateAriaValueNow = useCallback(primarySectionIndex => {
    if (props.sections) {
      const actSizes = getSectionSizes();
      // We need to check if the first\last sections have minSizes as zero\undefined
      let isFirstOrLastSection, isMinSizeZeroOrUndefined;
      const primarySection = props.sections[primarySectionIndex];
      isFirstOrLastSection = primarySectionIndex === 0 || primarySectionIndex === props.sections.length - 1;
      isMinSizeZeroOrUndefined = !primarySection.minSize || primarySection.minSize === 0;
      const primarySectionMinSize = primarySection?.fullyCollapsible || isFirstOrLastSection && isMinSizeZeroOrUndefined ? 0 : Math.max(0, paddingSize, convertCSSSizeToNumber(primarySection?.minSize));
      const secondarySectionIndex = sectionBarPositions[primarySectionIndex] === 'right' ? primarySectionIndex + 1 : primarySectionIndex - 1;
      const secondarySection = props.sections[secondarySectionIndex];
      isFirstOrLastSection = secondarySectionIndex === 0 || secondarySectionIndex === props.sections.length - 1;
      isMinSizeZeroOrUndefined = !secondarySection.minSize || secondarySection.minSize === 0;
      const secondarySectionMinSize = secondarySection?.fullyCollapsible || isFirstOrLastSection && isMinSizeZeroOrUndefined ? 0 : Math.max(0, paddingSize, convertCSSSizeToNumber(secondarySection?.minSize));
      const totalAvailableSize = actSizes[primarySectionIndex] + actSizes[secondarySectionIndex] - primarySectionMinSize - secondarySectionMinSize;
      return Math.round(totalAvailableSize > 0 ? 100 * (actSizes[primarySectionIndex] - primarySectionMinSize) / totalAvailableSize : 0);
    }
  }, [getSectionSizes, paddingSize, props.sections, sectionBarPositions]);

  // State variable to store the aria-valuenow for all the splitter bars
  const [barsAriaValueNow, setBarsAriaValueNow] = useState([]);
  /**
   * This useEffect will calculate the aria-valuenow for all the splitter bars
   * as soon as the section sizes change.
   */
  useEffect(() => {
    const calculatedValuesArray = props.sections ? props.sections.map((_section, index) => {
      const isBarPresent = sectionBarPositions[index] === 'right' || sectionBarPositions[index] === 'left';
      if (isBarPresent) {
        return calculateAriaValueNow(index) ?? 0;
      }
    }) : [];
    setBarsAriaValueNow(calculatedValuesArray);
  }, [props.sections, sizes, sectionBarPositions, calculateAriaValueNow]);
  return /*#__PURE__*/_jsx("div", {
    ref: element => {
      setSplitterContainer(element);
    },
    className: cx(cn.container[props.orientation || 'horizontal'], props.className),
    style: {
      ...splitterTokens,
      ...props.style
    },
    children: Children.map(props.children, (child, index) => {
      const section = props.sections?.[index];

      // NOTE: when we are not dragging, convert all sizes to percents.
      //     | this allows the proportions to be preserved during a resize.
      //     | this is also more efficient than running JS during a resize to "follow" layout.
      // FIXME: UX Spec does not allow proportion to be maintained for defaultSizes that are absolute values
      //      | we can probably support that by default (but proportion maintaining could be a new feature?)
      const size = sizes[index] < 0 ? undefined : isDragging || containerSize === undefined ? `${sizes[index]}px` : `${sizes[index] / containerSize * 100}%`;
      const minSize = section?.fullyCollapsible ? 0 : convertCSSSizeToNumber(section?.minSize);
      const isCollapsed = sizes[index] === -1 ? false : sizes[index] - minSize < 0.0001;
      const isFullyCollapsed = !!size && parseInt(size) === 0;
      const isLastSection = index === (props.sections ? props.sections.length - 1 : 0);
      const isBarPresent = sectionBarPositions[index] === 'right' || sectionBarPositions[index] === 'left';
      let barAriaLabel, barAriaLabelledBy;
      if (isBarPresent) {
        if (props.splitterBarAriaLabelledBy && props.splitterBarAriaLabelledBy.length > 0) {
          barAriaLabelledBy = props.splitterBarAriaLabelledBy[barCount];
        }
        if (props.splitterBarAriaLabels && props.splitterBarAriaLabels.length > 0 && !barAriaLabelledBy) {
          barAriaLabel = props.splitterBarAriaLabels[barCount] ?? localize('splitterBar.default.aria', {
            count: `${barCount + 1}`
          });
        } else if (!barAriaLabelledBy) {
          barAriaLabel = localize('splitterBar.default.aria', {
            count: `${barCount + 1}`
          });
        }
        barCount++;
      }
      return /*#__PURE__*/_jsx(SplitterSection, {
        barAriaLabel: barAriaLabel,
        barAriaLabelledBy: barAriaLabelledBy,
        barAriaValueNow: barsAriaValueNow[index],
        sectionAriaLabel: section?.ariaLabel,
        sectionAriaLabelledBy: section?.ariaLabelledBy,
        sectionAriaDescribedBy: section?.ariaDescribedBy,
        sectionLandmarkLabel: section?.landmarkLabel,
        testId: props.testId,
        paneIndex: index,
        isLastSection: isLastSection,
        minSize: convertCSSSizeToNumber(section?.minSize),
        ref: createSetRef(index),
        className: cx(section?.className, index !== 0 && index !== Children.count(props.children) - 1 && cn.hasPadding, isFullyCollapsed && cn.collapsed) // give the center sections a padding to avoid overlap
        ,
        orientation: orientation,
        size: size,
        barPos: sectionBarPositions[index],
        resizable: section?.resizable,
        hideSplitterBar: (section?.hideSplitterBar || props.hideSplitterBar) ?? false,
        collapsible: !!section?.collapsible,
        fullyCollapsible: !!section?.fullyCollapsible,
        isCollapsed: isCollapsed,
        isDragging: isDragging,
        onDragStart: () => {
          const next = getSectionSizes();
          if (next[index] !== 0 && next[index] !== convertCSSSizeToNumber(section?.minSize)) {
            previousSizes[index] = next[index];
          }
          setSizes(({
            previousSizes
          }) => ({
            sizes: next,
            previousSizes
          }));
          setIsDragging(true);
        },
        onDragEnd: cause => {
          if (canPaneSizesBePersisted) {
            if (cause === 'toggle') {
              sectionToggled.current = true;
            } else {
              sectionResized.current = true;
            }
          }
          setIsDragging(false);
        },
        onDrag: deltaPX => {
          const direction = sectionBarPositions[index];
          if (direction) {
            if (orientation === 'horizontal') {
              deltaPX = isRTL ? -deltaPX : deltaPX;
            }
            return resizeSection({
              index,
              direction,
              deltaPX
            });
          }
          return false;
        },
        onToggleCollapsed: () => {
          togglePane(index);
        },
        onKeyDown: event => {
          if (section && section.resizable === false && event.key !== ' ' && event.key !== 'Enter') return;
          const direction = sectionBarPositions[index];
          switch (event.key) {
            case 'ArrowLeft':
              {
                if (orientation === 'horizontal' && direction) {
                  if (direction === 'right') resizeSection({
                    index,
                    direction,
                    deltaPX: isRTL ? 20 : -20
                  });else resizeSection({
                    index,
                    direction,
                    deltaPX: isRTL ? -20 : 20
                  });
                  sectionResized.current = true;
                }
                break;
              }
            case 'ArrowRight':
              {
                if (orientation === 'horizontal' && direction) {
                  if (direction === 'right') resizeSection({
                    index,
                    direction,
                    deltaPX: isRTL ? -20 : 20
                  });else resizeSection({
                    index,
                    direction,
                    deltaPX: isRTL ? 20 : -20
                  });
                  sectionResized.current = true;
                }
                break;
              }
            case 'ArrowUp':
              {
                if (orientation === 'vertical' && direction) {
                  if (direction === 'right') resizeSection({
                    index,
                    direction,
                    deltaPX: -20
                  });else resizeSection({
                    index,
                    direction,
                    deltaPX: 20
                  });
                  sectionResized.current = true;
                }
                break;
              }
            case 'ArrowDown':
              {
                if (orientation === 'vertical' && direction) {
                  if (direction === 'right') resizeSection({
                    index,
                    direction,
                    deltaPX: 20
                  });else resizeSection({
                    index,
                    direction,
                    deltaPX: -20
                  });
                  sectionResized.current = true;
                }
                break;
              }
            case ' ':
            case 'Enter':
              {
                if (section && (section.collapsible || section.fullyCollapsible) && (index === 0 || isLastSection)) togglePane(index);
                break;
              }
            case 'Home':
              {
                if (direction) resizeSection({
                  index,
                  direction,
                  deltaPX: -9999
                });
                sectionToggled.current = true;
                props.onExpandCollapse?.(!isCollapsed, index);
                break;
              }
            case 'End':
              {
                if (direction) resizeSection({
                  index,
                  direction,
                  deltaPX: 9999
                });
                sectionToggled.current = true;
                props.onExpandCollapse?.(!isCollapsed, index);
                break;
              }
            default:
              return;
          }
          event.preventDefault();
        },
        children: child
      }, index);
    })
  });
});
//# sourceMappingURL=Splitter.js.map